'use client';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { HiSearch } from 'react-icons/hi';

import { SessionContext } from '@benzinga/session-context';
import { DelayedQuote, Logo, QuotesManager } from '@benzinga/quotes-manager';
import { Pagination } from '@benzinga/core-ui';
import Image from 'next/image';
import { PAGE_SIZE } from '../constants';

const getLogoUrl = (logo?: Logo | string | null): string | null => {
  if (!logo) return null;
  if (typeof logo === 'string') return logo;
  return logo?.files?.mark_composite_light ?? logo?.files?.mark_composite_dark ?? null;
};

interface ReportsListProps {
  allTickers: string[];
  initialDetails?: Record<string, { companyStandardName?: string }>;
  initialLogos?: Record<string, string | null>;
}

export const ReportsList = ({ allTickers, initialDetails, initialLogos }: ReportsListProps) => {
  const session = useContext(SessionContext);
  const quotesManager = session.getManager(QuotesManager);

  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [filteredTickers, setFilteredTickers] = useState(allTickers);
  const [currentPage, setCurrentPage] = useState(1);
  const [listDetails, setListDetails] = useState<Record<string, DelayedQuote> | null>(initialDetails as any ?? null);
  const [logos, setLogos] = useState<Record<string, Logo>>(initialLogos as any ?? {});

  const currentTickers = useMemo(
    () => filteredTickers.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filteredTickers, currentPage],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue]);

  const isInitialRender = useRef(true);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    const value = debouncedSearchValue.trim();
    setCurrentPage(1);
    setListDetails(null);
    if (!value) {
      setFilteredTickers(allTickers);
      return;
    }
    const upper = value.toUpperCase();
    const startsWith = allTickers.filter(t => t.startsWith(upper));
    const startsWithSet = new Set(startsWith);
    const includes = allTickers.filter(t => t.includes(upper) && !startsWithSet.has(t));
    setFilteredTickers([...startsWith, ...includes]);
  }, [debouncedSearchValue, allTickers]);

  const handleSearch = (val: string) => {
    setSearchValue(val);
  };

  const handlePageChange = ({ nextPage }) => {
    setCurrentPage(nextPage);
    setListDetails(null);
  };

  const fetchDelayedQuotes = useCallback(
    async (tickers: string[]) => {
      try {
        const res = await quotesManager.getDelayedQuotes(tickers);
        return res.ok;
      } catch {
        return undefined;
      }
    },
    [quotesManager],
  );

  const fetchLogos = useCallback(
    async (tickers: string[]) => {
      try {
        const res = await quotesManager.getQuotesLogosMapped(tickers);
        return res.ok;
      } catch {
        return undefined;
      }
    },
    [quotesManager],
  );

  useEffect(() => {
    let stale = false;
    if (currentTickers.length > 0) {
      fetchDelayedQuotes(currentTickers).then(details => {
        if (!stale) setListDetails(details ?? {});
      });
      fetchLogos(currentTickers).then(result => {
        if (!stale && result) setLogos(prev => ({ ...prev, ...result }));
      });
    }
    return () => { stale = true; };
  }, [currentTickers, fetchDelayedQuotes, fetchLogos]);

  const handleClear = () => {
    setSearchValue('');
    setDebouncedSearchValue('');
  };

  return (
    <>
    <div>
      <div className="mb-6">
        <div className="flex items-center border border-slate-200 rounded-lg bg-white focus-within:border-blue-400 transition-all">
          <HiSearch className="text-slate-400 text-lg ml-4 shrink-0" />
          <input
            aria-label="Search reports by ticker"
            className="flex-1 py-3 px-3 text-base bg-transparent border-none outline-none placeholder:text-slate-400"
            onChange={e => handleSearch(e.target.value)}
            placeholder="Search by ticker symbol..."
            type="text"
            value={searchValue}
          />
          {searchValue.length > 0 && (
            <button
              aria-label="Clear search"
              className="p-1.5 mr-3 hover:bg-slate-100 rounded-md transition-colors cursor-pointer"
              onClick={handleClear}
              type="button"
            >
              <svg className="w-4 h-4 text-slate-400" fill="currentColor" viewBox="0 0 20 20">
                <path
                  clipRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  fillRule="evenodd"
                />
              </svg>
            </button>
          )}
        </div>
        <p className="text-center text-sm text-slate-500 mt-3">
          {debouncedSearchValue.trim()
            ? `${filteredTickers.length} report${filteredTickers.length !== 1 ? 's' : ''} matching "${debouncedSearchValue.trim()}"`
            : `${allTickers.length} stock reports available`}
        </p>
      </div>

      {currentTickers.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {currentTickers.map(ticker => {
            const companyName = listDetails?.[ticker]?.companyStandardName;
            const isLoading = listDetails === null;
            const logoUrl = getLogoUrl(logos[ticker]);

            return (
              <Link
                className="group flex items-center gap-3 bg-white border border-[#CEDDF2] rounded-lg p-4 hover:bg-[#F2F8FF] hover:shadow-sm transition-all"
                href={`/quote/${ticker}/report`}
                key={ticker}
                prefetch={false}
              >
                <div className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden">
                  {logoUrl ? (
                    <Image
                      alt={`${ticker} logo`}
                      className="w-8 h-8 object-contain"
                      height={32}
                      src={logoUrl}
                      width={32}
                    />
                  ) : (
                    <span className="text-sm font-bold text-slate-500">{ticker.slice(0, 4)}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="block text-sm font-bold text-slate-800 group-hover:text-blue-600 transition-colors">
                    {ticker}
                  </span>
                  {isLoading ? (
                    <span className="block h-4 w-32 bg-slate-100 rounded animate-pulse mt-1" />
                  ) : (
                    <span className="block text-xs text-slate-500 truncate mt-0.5">
                      {companyName || 'View Report'}
                    </span>
                  )}
                </div>
                <svg
                  className="w-4 h-4 text-slate-300 group-hover:text-blue-500 transition-colors shrink-0"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center text-center py-12">
          <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
            <HiSearch className="text-xl text-slate-400" />
          </div>
          <p className="text-base font-semibold text-slate-700 mb-1">
            No reports found for &ldquo;{searchValue}&rdquo;
          </p>
          <p className="text-sm text-slate-500">Try a different ticker symbol</p>
        </div>
      )}

      {filteredTickers.length > PAGE_SIZE && (
        <div className="flex justify-center mt-6">
          <Pagination
            buttonClassName="!rounded-lg !bg-white !border-[#CEDDF2] hover:!bg-[#F2F8FF] hover:!text-blue-600 [&.active]:!bg-blue-600 [&.active]:!text-white [&.active]:!border-blue-600"
            defaultPage={currentPage}
            onPageChanged={handlePageChange}
            pageSize={PAGE_SIZE}
            totalItems={filteredTickers.length}
          />
        </div>
      )}
    </div>
    </>
  );
};  

export default ReportsList;
